import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import styles from "../../assets/styles/Homepage/WhatWeOffer.module.css";
import {
  FaBook,
  FaAward,
  FaUserGraduate,
  FaHeart,
  FaStar,
  FaLeaf,
  FaMedal,
  FaBrain,
  FaTimes,
} from "react-icons/fa";
import dividerIcon from "../../assets/images/linedesign.png";

/* ================= API ================= */
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
/* ================= ICON MAP ================= */
const ICON_MAP = {
  FaBook: <FaBook />,
  FaAward: <FaAward />,
  FaUserGraduate: <FaUserGraduate />,
  FaHeart: <FaHeart />,
  FaStar: <FaStar />,
  FaLeaf: <FaLeaf />,
  FaMedal: <FaMedal />,
  FaBrain: <FaBrain />,
};

/* ================= HELPERS ================= */
const parseFeatures = (features) => {
  if (Array.isArray(features)) return features;
  return features?.split(",").map((f) => f.trim()) || [];
};

/* ================= ANIMATIONS ================= */
const headingVariant = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const containerVariant = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } },
};

const cardVariant = {
  hidden: { opacity: 0, y: 35, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6 },
  },
};

const iconHover = {
  hover: {
    scale: 1.15,
    rotate: [0, -5, 5, 0],
    transition: { duration: 0.4 },
  },
};

const modalVariant = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 },
};

const overlayVariant = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

/* ================= COMPONENT ================= */
const WhatWeOffer = () => {
  const [data, setData] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${ENV_API_BASE_URL}/api/home-what-we-offer`);

      const sorted = (res.data.data || []).sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );

      setData(sorted);
    } catch (err) {
      console.error("Failed to load data", err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item) => {
    setSelectedCard(item);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedCard(null);
    document.body.style.overflow = "auto";
  };

  return (
    <motion.section
      className={styles.section}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className="container">
        {/* Heading */}
        <motion.div className={styles.headingWrapper} variants={headingVariant}>
          <h2 className={styles.heading}>What We Offer ?</h2>

          <div className={styles.divider}>
            <span className={styles.line} />
            <img src={dividerIcon} alt="Divider" className={styles.dividerImage} />
            <span className={styles.line} />
          </div>
        </motion.div>

        {/* Cards */}
        <motion.div className={styles.cards} variants={containerVariant}>
          {loading ? (
            <p>Loading...</p>
          ) : data.length === 0 ? (
            <p>No data found</p>
          ) : (
            data.map((item) => (
              <motion.div
                className={styles.card}
                key={item._id}
                variants={cardVariant}
                whileHover={{
                  y: -8,
                  boxShadow: "0 15px 35px rgba(0,0,0,0.15)",
                }}
                onClick={() => openModal(item)}
              >
                {/* Icon */}
                <div className={styles.iconWrapper}>
                  <motion.div
                    className={styles.icon}
                    variants={iconHover}
                    whileHover="hover"
                  >
                    {ICON_MAP[item.icon] || <FaBook />}
                  </motion.div>
                </div>

                {/* Title */}
                <h3 className={styles.cardTitle}>
                  {item.title?.split("\n").map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
                </h3>

                {/* Text */}
                <p className={styles.cardText}>{item.text}</p>

                <div className={styles.learnMore}>Learn More →</div>
              </motion.div>
            ))
          )}
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedCard && (
          <>
            <motion.div
              className={styles.modalOverlay}
              variants={overlayVariant}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={closeModal}
            />

            <motion.div
              className={styles.modalWrapper}
              variants={modalVariant}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => {
                if (e.target === e.currentTarget) closeModal();
              }}
            >
              <div className={styles.modalContainer}>
                <div className={styles.modal}>
                  <button
                    className={styles.closeButton}
                    onClick={closeModal}
                  >
                    <FaTimes />
                  </button>

                  <div className={styles.modalContent}>
                    {/* LEFT */}
                    <div className={styles.modalLeft}>
                      <div className={styles.modalIcon}>
                        {ICON_MAP[selectedCard.icon]}
                      </div>

                      <h2 className={styles.modalTitle}>
                        {selectedCard.modalContent?.title}
                      </h2>

                      <p className={styles.modalDescription}>
                        {selectedCard.modalContent?.description}
                      </p>

                      <div className={styles.modalFeatures}>
                        <h4>Key Features:</h4>
                        <ul>
                          {parseFeatures(
                            selectedCard.modalContent?.features
                          ).map((f, i) => (
                            <li key={i}>✓ {f}</li>
                          ))}
                        </ul>
                      </div>

                      <button className={styles.ctaButton}>
                        Get Started Today
                      </button>
                    </div>

                    {/* RIGHT IMAGE */}
                    <div className={styles.modalRight}>
                      <img
  src={
    selectedCard?.image
      ? `${ENV_API_BASE_URL}/uploads/${selectedCard.image}`
      : "https://via.placeholder.com/400"
  }
  alt={selectedCard?.title || "image"}
  className={styles.modalImage}
/>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default WhatWeOffer;